import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
// HOCs
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withPermissions from 'hocs/WithPermissions';
// Models
import mSlug from 'models/cpm/slug';
// Custom
import { buildListSpecs } from 'utils/Tools';
import { fullListShape, cpmWorkspaceGroupForSlugShape } from 'constants/PropTypesShapes';
import {
  refreshList,
  openDialog,
  closeDialog,
  cpmListSlugs,
  cpmCreateSlug,
  cpmUpdateSlug,
  cpmDeleteSlug,
} from 'actions';
import SubListCardTemplate from 'components/templates/SubListCardTemplate';
import { AddContainedButton } from 'components/buttons/ContainedButtons';
import SlugRow from 'cpmWorkspaces/assets/slug/SlugRow';
import SlugFilters from 'cpmWorkspaces/assets/slug/SlugFilters';

class SlugManagement extends React.Component {
  constructor(props) {
    super(props);

    this.handleAddSlug = this.handleAddSlug.bind(this);
    this.handleEditSlug = this.handleEditSlug.bind(this);
    this.handleDeleteSlug = this.handleDeleteSlug.bind(this);
    this.handleValidateCreateSlug = this.handleValidateCreateSlug.bind(this);
    this.handleValidateDeleteSlug = this.handleValidateDeleteSlug.bind(this);
    this.handleValidateUpdateSlug = this.handleValidateUpdateSlug.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);

    // Build list specs: permanent filters
    const { permanentFiltersSpecs } = this.props;
    this.listSpecs = buildListSpecs(mSlug.listFeatures.searchFields, permanentFiltersSpecs);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  componentDidUpdate(prevProps) {
    const {
      slugCreated, slugDeleted, slugUpdated, handleRefreshList, handleCloseDialog,
    } = this.props;
    const { slugCreated: prevSlugCreated, slugDeleted: prevSlugDeleted, slugUpdated: prevSlugUpdated } = prevProps;

    // Catch operations success which need a refresh of the list
    if ((!prevSlugCreated && slugCreated) || (!prevSlugUpdated && slugUpdated)) {
      handleRefreshList();

      // Close popup
      handleCloseDialog('SLUG_ADD_EDIT');
    }

    if (!prevSlugDeleted && slugDeleted) {
      handleRefreshList();
    }
  }

  // Handlers
  handleAddSlug() {
    const { handleOpenDialog, workspaceGroup } = this.props;

    handleOpenDialog({
      title: {
        id: 'title.addSlug',
      },
      actions: [{ type: 'cancel' }, { type: 'validate', preventClosing: true }],
      component: {
        type: 'SLUG_ADD_EDIT',
        props: {
          onValidate: (data) => this.handleValidateCreateSlug({ ...data, workspaceGroupId: workspaceGroup.id }),
        },
      },
      dividers: true,
      size: 'md',
    });
  }

  handleEditSlug(slug) {
    const { handleOpenDialog } = this.props;

    handleOpenDialog({
      title: {
        id: 'title.editSlug',
      },
      actions: [{ type: 'cancel' }, { type: 'validate', preventClosing: true }],
      component: {
        type: 'SLUG_ADD_EDIT',
        props: {
          slug,
          onValidate: (data) => this.handleValidateUpdateSlug({
            ...data,
            id: slug.id,
          }),
        },
      },
      dividers: true,
      size: 'md',
    });
  }

  handleDeleteSlug(slug) {
    const { handleDisplayConfirm } = this.props;

    handleDisplayConfirm(
      {
        id: 'confirm.deleteSlug',
      },
      () => this.handleValidateDeleteSlug({
        id: slug.id,
      }),
      { confirmLabel: 'confirm' },
    );
  }

  handleValidateCreateSlug(data) {
    const { handleAddSlug } = this.props;
    handleAddSlug(data);
  }

  handleValidateUpdateSlug(data) {
    const { handleUpdateSlug } = this.props;
    handleUpdateSlug(data);
  }

  handleValidateDeleteSlug(data) {
    const { handleDeleteSlug } = this.props;
    handleDeleteSlug(data);
  }

  doGetRowComp(props) {
    return <SlugRow {...props} onEdit={this.handleEditSlug} onDelete={this.handleDeleteSlug} />;
  }

  render() {
    const { listSlugs, handleListSlugs } = this.props;
    const { permanentFilters } = this.listSpecs;

    return (
      <React.Fragment>
        <div className="d-flex justify-content-end m-3">
          <AddContainedButton size="small" primary onClick={this.handleAddSlug} />
        </div>
        <SubListCardTemplate
          name="slug"
          listFeatures={mSlug.listFeatures}
          withQueryParams={false}
          withActions
          nbActions={2}
          permanentFilters={permanentFilters}
          elementsData={{
            loading: listSlugs.loading,
            elements: listSlugs.elements,
            handleGetList: handleListSlugs,
          }}
          filtersComp={<SlugFilters noPadding />}
          rowComp={this.doGetRowComp}
        />
      </React.Fragment>
    );
  }
}

SlugManagement.propTypes = {
  onRef: PropTypes.func.isRequired,
  listSlugs: fullListShape.isRequired,
  slugCreated: PropTypes.bool.isRequired,
  slugDeleted: PropTypes.bool.isRequired,
  slugUpdated: PropTypes.bool.isRequired,
  permanentFiltersSpecs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ),
  workspaceGroup: cpmWorkspaceGroupForSlugShape.isRequired,
  handleRefreshList: PropTypes.func.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleListSlugs: PropTypes.func.isRequired,
  handleAddSlug: PropTypes.func.isRequired,
  handleUpdateSlug: PropTypes.func.isRequired,
  handleDeleteSlug: PropTypes.func.isRequired,
};

SlugManagement.defaultProps = {
  permanentFiltersSpecs: null,
};

const mapStateToProps = (state) => ({
  listSlugs: state.cpmSlug.list,
  slugCreated: state.cpmSlug.add.added,
  slugDeleted: state.cpmSlug.delete.deleted,
  slugUpdated: state.cpmSlug.update.updated,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  handleListSlugs: (pagination, sort, filters) => {
    dispatch(
      cpmListSlugs(mSlug.queListSlugs(ownProps.hasPermission), {
        pagination,
        sort,
        filters,
      }),
    );
  },
  handleAddSlug: (data) => {
    dispatch(cpmCreateSlug(mSlug.mutCreateSlug, data));
  },
  handleUpdateSlug: (data) => {
    dispatch(cpmUpdateSlug(mSlug.mutUpdateSlug, data));
  },
  handleDeleteSlug: (data) => {
    dispatch(cpmDeleteSlug(mSlug.mutDeleteSlug, data));
  },
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  handleCloseDialog: (componentType) => {
    dispatch(closeDialog(componentType));
  },
  handleRefreshList: () => {
    dispatch(refreshList());
  },
});

module.exports = compose(
  withPermissions,
  withConfirmDialog,
  connect(mapStateToProps, mapDispatchToProps),
)(SlugManagement);



// WEBPACK FOOTER //
// ./routes/cpm/routes/workspaces/assets/slug/SlugManagement.js